import type { AdditionalFailure, Attempt } from './runs'

/**
 * The runner's canonical failure vocabulary, for display.
 *
 * Source of truth: `experiment_ctl/src/experiment_ctl/task_delivery/errors.py`
 * in seldon-rl-env-cua. Every attempt record in `outcomes/<job>/<attempt>.json`
 * carries `error_class` (one of these keys), `sample_outcome`, `retry_decision`
 * and `node_action`; this file only adds the wording and the flags the page
 * needs to explain them. Keep the keys and flags in step with that module.
 *
 * Runs launched by older runner releases carry the pre-error-model vocabulary,
 * so LEGACY maps those onto the same classes. An unrecognised key is never
 * hidden: it is shown verbatim and marked as unknown to this build.
 */

export type FailureLayer =
  | 'transport' | 'hook' | 'contract' | 'sampler' | 'model'
  | 'openrouter' | 'model_cli' | 'worker' | 'collect' | 'publish' | 'verify' | 'fleet' | 'runtime'

export interface FailureClass {
  key: string
  label: string
  layer: FailureLayer
  meaning: string
  /** The runner may retry it; a non-transient class is never retried. */
  transient: boolean
  /** Counts towards the node's quarantine streak: the machine may be at fault. */
  nodeAttributable: boolean
  /** Once the model has started, the attempt still yields a usable score. */
  scoreable: boolean
  /** The task, image, or release definition is wrong — not an infra flake. */
  definitionDefect: boolean
}

export const LAYERS: Record<FailureLayer, { label: string; blame: string }> = {
  worker: { label: 'Worker', blame: 'the worker process' },
  model_cli: { label: 'Model CLI', blame: 'the model command-line client' },
  openrouter: { label: 'OpenRouter', blame: 'the model gateway or upstream provider' },
  transport: { label: 'Guest transport', blame: 'the channel between the worker and the guest VM' },
  hook: { label: 'Task hooks', blame: "the task's own lifecycle hooks" },
  contract: { label: 'Definition', blame: 'the task, image, or release definition' },
  sampler: { label: 'Model API', blame: 'the model provider' },
  model: { label: 'Model budget', blame: "the rollout's own budget" },
  collect: { label: 'Evidence', blame: 'collecting the episode bundle' },
  publish: { label: 'Publication', blame: 'uploading the episode bundle' },
  verify: { label: 'Verifier', blame: "the task's verifier" },
  fleet: { label: 'Fleet', blame: 'the worker node itself' },
  runtime: { label: 'Runtime', blame: 'something the runner could not classify' },
}

function cls(
  key: string, label: string, meaning: string,
  flags: Partial<Pick<FailureClass, 'transient' | 'nodeAttributable' | 'scoreable' | 'definitionDefect'>> = {},
): FailureClass {
  return {
    key, label, meaning, layer: key.split('.')[0] as FailureLayer,
    transient: false, nodeAttributable: false, scoreable: false, definitionDefect: false, ...flags,
  }
}

const T = { transient: true }
const TN = { transient: true, nodeAttributable: true }

export const FAILURE_CLASSES: FailureClass[] = [
  cls('openrouter.connection_failed', 'Model connection failed', 'The connection to OpenRouter could not be established or was interrupted.'),
  cls('runtime.unhandled_exception', 'Unhandled runtime exception', 'The runtime encountered an exception without a more specific diagnostic code.'),
  cls('worker.process_exited', 'Worker process exited', 'The worker process exited before completing the attempt.'),
  cls('model_cli.process_failed', 'Model client process failed', 'The model command-line client exited unsuccessfully without a structured provider response.'),
  cls('openrouter.authentication_failed', 'Authentication failed', 'OpenRouter rejected the credentials.'),
  cls('openrouter.credits_exhausted', 'Credits exhausted', 'OpenRouter reported insufficient credit.'),
  cls('openrouter.budget_exceeded', 'Spending limit reached', 'The configured spending limit was reached.'),
  cls('openrouter.access_denied', 'Model access denied', 'The model request was denied by an account or provider restriction.'),
  cls('openrouter.request_invalid', 'Invalid model request', 'The model request has invalid or unsupported parameters.'),
  cls('openrouter.output_budget_exceeded', 'Output budget exceeded', 'The requested output exceeds an explicit provider limit.'),
  cls('openrouter.context_exceeded', 'Context limit exceeded', 'The request exceeds the model context limit.'),
  cls('openrouter.route_unavailable', 'No eligible provider', 'OpenRouter could not route this request to an eligible endpoint.'),
  cls('openrouter.rate_limited', 'Model rate limited', 'OpenRouter or the upstream provider throttled the request.'),
  cls('openrouter.upstream_unavailable', 'Upstream provider unavailable', 'The selected provider failed or was unavailable.'),
  cls('openrouter.timeout', 'Model request timed out', 'The model request did not complete in time.'),
  cls('openrouter.policy_blocked', 'Model policy blocked request', 'The model gateway or provider explicitly blocked the request or refused it.'),
  cls('openrouter.response_invalid', 'Invalid model response', 'The response could not be read in the expected format.'),
  cls('openrouter.stream_interrupted', 'Model stream interrupted', 'The response stream ended before completion.'),
  cls('openrouter.empty_response', 'Empty model response', 'No usable output was returned and no more specific cause was recorded.'),

  cls('transport.connect_failed', 'Guest unreachable', 'The worker could not open its control channel to the guest VM.', TN),
  cls('transport.timeout', 'Guest timed out', 'The guest stopped answering the control channel in time.', TN),
  cls('transport.file_transfer_failed', 'File transfer failed', 'Moving a file to or from the guest failed.', TN),
  cls('transport.health_monitor', 'Health monitor lost', 'The guest health monitor became unreachable mid-episode.', TN),
  cls('hook.exit_nonzero', 'Hook exited non-zero', "One of the task's lifecycle hooks failed.", T),
  cls('hook.response_malformed', 'Hook response malformed', 'A task hook returned a response the runner could not read.', T),
  cls('hook.collector_fatal', 'Collector hook failed', "The task's evidence collector hook failed outright."),
  cls('contract.invalid', 'Definition invalid', 'The task, image, or release definition is wrong. The sample is invalid and is never retried.', { definitionDefect: true }),
  cls('sampler.rate_limited', 'Model rate-limited', 'The model API rate-limited the request (HTTP 429).', T),
  cls('sampler.billing_auth', 'Model billing or auth refused', 'The model API refused the request (401/402/403). The worker pauses for 15 minutes instead of retrying.'),
  cls('sampler.provider_error', 'Model provider error', 'The model API returned an error.', T),
  cls('model.budget_exhausted', 'Model budget exhausted', 'The rollout spent its whole model budget; the attempt still scores.', { scoreable: true }),
  cls('collect.export_failed', 'Export failed', "Exporting the episode's evidence from the guest failed."),
  cls('collect.integrity_mismatch', 'Evidence integrity mismatch', 'Collected evidence did not match its declared checksum.'),
  cls('collect.budget_exhausted', 'Collection budget exhausted', 'Evidence collection ran out of its time or size budget.'),
  cls('collect.no_bundle', 'No episode bundle', 'The attempt finished without producing an episode bundle.', TN),
  cls('publish.upload_failed', 'Upload failed', 'Uploading the episode bundle to S3 failed.', { ...TN, scoreable: true }),
  cls('verify.sandbox_unavailable', 'Verifier sandbox unavailable', "The verifier's sandbox could not start on the node.", { nodeAttributable: true }),
  cls('verify.timeout', 'Verifier timed out', 'The verifier did not finish within its budget.'),
  cls('verify.crashed', 'Verifier crashed', "The task's verifier crashed. This is a defect in the task, not the run.", { definitionDefect: true }),
  cls('verify.envelope_invalid', 'Verifier result invalid', 'The verifier returned a result envelope that does not match the contract.', { definitionDefect: true }),
  cls('fleet.node_lost', 'Node lost', 'The node vanished mid-attempt — a spot interruption or a crash — without publishing a record.'),
  cls('runtime.unknown', 'Unclassified', 'The runner could not attribute this failure to a boundary.'),
]

const BY_KEY = new Map(FAILURE_CLASSES.map(item => [item.key, item]))

/** Pre-error-model classes, as older runner releases still write them. */
const LEGACY: Record<string, string> = {
  attempt_infra: 'runtime.unknown',
  eval_timeout: 'runtime.unknown',
  export_error: 'collect.export_failed',
  export_failed: 'collect.export_failed',
  grading_error: 'verify.crashed',
  health_monitor_unavailable: 'transport.health_monitor',
  no_episode_bundle: 'collect.no_bundle',
  runtime_failure: 'runtime.unknown',
  sampler_error: 'sampler.provider_error',
  sampler_failure: 'sampler.provider_error',
  sampler_http_401: 'sampler.billing_auth',
  sampler_http_402: 'sampler.billing_auth',
  sampler_http_403: 'sampler.billing_auth',
  sampler_http_429: 'sampler.rate_limited',
  unknown_infra: 'runtime.unknown',
  verifier_infrastructure: 'verify.sandbox_unavailable',
}
/** Legacy classes with no canonical equal; kept so their wording is not lost. */
const LEGACY_ONLY: Record<string, { label: string; meaning: string }> = {
  blank_ui: { label: 'Application never rendered', meaning: 'The first and last frames were both blank: the episode ran against a dead UI.' },
}

export interface ResolvedFailure extends FailureClass {
  /** Exactly what the runner wrote, always shown somewhere. */
  raw: string
  /** Recorded by a runner release older than the canonical error model. */
  legacy: boolean
  /** False when this build has never heard of the class. */
  known: boolean
}

export function describeFailure(errorClass?: string | null): ResolvedFailure | undefined {
  if (!errorClass) return undefined
  const raw = errorClass.trim()
  if (!raw) return undefined
  const canonical = BY_KEY.get(raw)
  if (canonical) return { ...canonical, raw, legacy: false, known: true }
  const aliased = BY_KEY.get(LEGACY[raw] ?? '')
  if (aliased) return { ...aliased, raw, legacy: true, known: true }
  const only = LEGACY_ONLY[raw]
  if (only) return { ...cls('runtime.unknown', only.label, only.meaning), raw, legacy: true, known: true }
  return {
    ...cls(raw, raw, 'This build does not know this failure class; the runner is newer than the page.'),
    layer: (LAYERS[raw.split('.')[0] as FailureLayer] ? raw.split('.')[0] : 'runtime') as FailureLayer,
    raw, legacy: false, known: false,
  }
}

/** What the attempt meant for the experiment's sample (errors.sample_outcome). */
export const SAMPLE_OUTCOMES: Record<string, { label: string; tone: 'ok' | 'warn' | 'err'; meaning: string }> = {
  scored: { label: 'Scored', tone: 'ok', meaning: 'The attempt produced a usable sample.' },
  infrastructure: { label: 'Infrastructure', tone: 'warn', meaning: 'Not a sample: infrastructure failed, so it is excluded from results.' },
  invalid: { label: 'Invalid', tone: 'err', meaning: 'Not a sample: the task or image definition is defective.' },
  not_run: { label: 'Not run', tone: 'warn', meaning: 'The attempt never started.' },
}

/** What the node decided to do with the job afterwards (node_agent.retry). */
export const RETRY_DECISIONS: Record<string, { label: string; meaning: string }> = {
  retry: { label: 'Queued for retry', meaning: 'Released back to the queue after a backoff.' },
  defer: { label: 'Deferred', meaning: 'Parked without spending an attempt: the retry budget is empty or the model API is paused.' },
  no_retry: { label: 'Not retried', meaning: 'The queue will not hand this job out again.' },
}

/** What the failure did to the node itself (node_agent.health). */
export const NODE_ACTIONS: Record<string, { label: string; meaning: string }> = {
  quarantine: { label: 'Node quarantined', meaning: 'Ten consecutive node-attributable failures: the node stopped taking work.' },
  pause_pulls: { label: 'Node paused', meaning: 'The model API refused billing or auth; the node stopped pulling jobs for 15 minutes.' },
}

/** Diagnostic identity is independent of the operational class used by retry policy. */
export function attemptFailureCode(attempt?: Attempt): string | undefined {
  return attempt?.diagnostic?.code || attempt?.failure_code || attempt?.error_class || undefined
}
export function attemptFailureMessage(attempt?: Attempt): string | undefined {
  return attempt?.diagnostic?.message || attempt?.detail || undefined
}
export function describeRetry(attempt: Attempt): { label: string; meaning: string } | undefined {
  const status = attempt.retry_status
  const retry = status === 'retried' ? { label: 'Retried', meaning: 'A later attempt was started.' }
    : status === 'queued' ? { label: 'Queued for retry', meaning: 'The job is queued for another attempt.' }
    : status === 'not_retrying' ? { label: 'Not retried', meaning: 'No further attempt is scheduled.' }
    : RETRY_DECISIONS[attempt.retry_decision ?? '']
  if (!retry) return undefined
  const reason = attempt.retry_reason === 'model_already_started' ? 'Automatic rollout retry blocked because model execution already started.'
    : attempt.retry_reason === 'cancelled' ? 'The job was cancelled.' : undefined
  return reason ? { ...retry, meaning: reason } : retry
}

/** The attempt's additional failures (upload, cleanup), with anything malformed dropped.
 *  They are part of the attempt they ride on and are never tallied as failures. */
export function additionalFailures(attempt?: Attempt): AdditionalFailure[] {
  const rows = Array.isArray(attempt?.additional_failures) ? attempt.additional_failures : []
  const text = (value: unknown) => typeof value === 'string' && value.trim() ? value : undefined
  return rows.flatMap(row => {
    if (!row || typeof row !== 'object' || !text(row.code)) return []
    return [{ code: row.code, message: text(row.message) ?? '',
      stage: row.stage === 'publication' || row.stage === 'cleanup' ? row.stage : null,
      diagnostic_id: text(row.diagnostic_id) ?? null, exception_type: text(row.exception_type) ?? null }]
  }).slice(0, 8)
}

/** Display only named scalar facts. Never render arbitrary payloads or headers. */
export function diagnosticFacts(attempt: Attempt): [string, string][] {
  const labels: Record<string, string> = {
    error_type: 'Canonical error', limit_source: 'Budget source', resolved_model: 'Resolved model',
    exit_code: 'Exit status', http_status: 'HTTP status', error_code: 'Gateway error code', provider_code: 'Provider error code',
    request_id: 'Request ID', model: 'Model', provider: 'Provider', finish_reason: 'Finish reason',
    native_finish_reason: 'Native finish reason', output_received: 'Output received',
  }
  return Object.entries(labels).flatMap(([key, label]) => {
    const value = attempt.diagnostic?.facts?.[key]
    return value == null || typeof value === 'object' ? [] : [[label, String(value)] as [string, string]]
  })
}

/** Episode endings that still produce a sample. The runtime ends the episode on purpose and
 *  scores it, so the attempt is a success for the queue and is never tallied as a failure,
 *  but why it ended is part of reading its score. */
export const SCORED_ENDINGS: Record<string, { label: string; meaning: string }> = {
  model_refused: { label: 'Model refused', meaning: 'The model refused the task. The episode ended and scored 0 without verification; this is a scored sample, not an infrastructure failure.' },
}

export interface ScoredNotice { label: string; meaning: string; code?: string; message?: string }

/** What a scored attempt recorded besides its score: a known scored ending (model_refused)
 *  or any diagnostic REMI attached. Failed and in-flight attempts are never notices. */
export function scoredNotice(attempt?: Attempt): ScoredNotice | undefined {
  if (!attempt || attempt.classification !== 'success') return undefined
  const ending = SCORED_ENDINGS[attempt.summary?.termination_reason ?? '']
  const diagnostic = attempt.diagnostic
  if (!ending && !diagnostic) return undefined
  return {
    label: ending?.label ?? 'Diagnostic recorded',
    meaning: ending?.meaning ?? 'The attempt scored, and the runtime recorded a diagnostic for it.',
    code: diagnostic?.code || attempt.failure_code || undefined,
    message: diagnostic?.message || undefined,
  }
}
